import { useLocalization } from '@lib/localization';
import type { Inspection } from '@lib/spproject/types';
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Pagination,
  Tooltip,
  Typography,
  colors
} from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import { Target } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { AngleDownIcon } from 'react-line-awesome';
import { Accordion, ChecklistHeader, ChecklistInfo } from './Checklist.styled';
import ChecklistPointItem from './ChecklistPointItem';

interface ChecklistAccordionProps {
  checklist: IInspection;
  inspections: IInspection[];
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onPoiClick: (poi: Inspection.PointOfInterest, template: IInspection) => void;
}

const POIS_PER_PAGE = 5;

const ChecklistAccordion = memo<ChecklistAccordionProps>(({
  checklist, inspections, isExpanded, onToggleExpand, onPoiClick
}) => {
  const { t } = useLocalization();
  const [page, setPage] = useState(1);

  const pois = useMemo(() => checklist.pointOfInterests ?? [], [checklist.pointOfInterests]);

  const poiCount = pois.length;
  const totalPages = Math.ceil(poiCount / POIS_PER_PAGE) || 1;
  const start = (page - 1) * POIS_PER_PAGE;

  const paginated = useMemo(() => {
    return pois.slice(start, start + POIS_PER_PAGE);
  }, [pois, start]);

  const handleToggle = useCallback(() => {
    onToggleExpand(checklist.info.id);
  }, [onToggleExpand, checklist.info.id]);

  const handlePageChange = useCallback((_: unknown, value: number) => {
    setPage(prev => (prev === value ? prev : value));
  }, []);

  useEffect(() => {
    if (isExpanded) setPage(1);
  }, [isExpanded]);

  return (
    <Accordion expanded={isExpanded} onChange={handleToggle}>
      <AccordionSummary expandIcon={<AngleDownIcon />}>
        <ChecklistHeader>
          <ChecklistInfo>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {checklist.info.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {checklist.info.id}
            </Typography>
          </ChecklistInfo>

          <Tooltip title={t('Project_TotalInspections')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 3 }}>
              <Target size={14} style={{ color: colors.blue[500] }} />
              <Typography variant="caption" fontWeight={500}>
                {inspections.length}
              </Typography>
            </Box>
          </Tooltip>
        </ChecklistHeader>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        <Box>
          {paginated.map((poi, i) => (
            <ChecklistPointItem
              key={poi.id}
              poi={poi}
              template={checklist}
              inspections={inspections}
              index={i}
              onClick={onPoiClick}
            />
          ))}

          {pois.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('POI_NotFound')}</Typography>
            </Box>
          )}

          {totalPages > 1 && (
            <>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
              </Box>
            </>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

export default ChecklistAccordion;
